import React, { useState } from "react";
import { createPortal } from "react-dom";
import { formatHandicapLabel, formatUnixToVNDate } from "@/utils/format";
import { convertMatchTime } from "@/utils/convertMatchTime";
import { LIVE_STATUSES, FINISHED_STATUSES } from "@/contants";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { getOddValue, colorOddsChanges, getMatchHeaderType } from "@/utils/matchInfo";
import KbvOddsStatus from "@/components/KbvOddsStatus";
import CarouselMatchTime from "./CarouselMatchTime";
import ICON_CORNER from "@/assets/images/corner.svg";
import ICON_TATICS from "@/assets/images/tatics.svg";
import ICON_NHANDINH from "@/assets/images/nhandinh.svg";
import ICON_DUDOAN_KIEMTIEN from "@/assets/images/mi-dudoan-kiemtien-green.svg";
import { KEOBONGVIP_DOMAIN } from "@/config";
import ThongTinTranDau from "@/components/ThongTinTranDau";

export default function CarouselMatch({ match }) {
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const liveScore = getScore();
  const liveOdds = getOdds();

  const statusNum = liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);

  // Filter odds với companyId = -1 (tất cả)
  const {
    filterHandicapOdds,
    filterOverUnderOdds,
    filterEuropeOdds,
    filterHandicapHalfOdds,
  } = useFilterOdds(match, liveOdds, null, -1);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const { date: formattedDate, thu: formattedThu, gio: formattedGio } = formatUnixToVNDate(match.matchTime);
  const isFinished = FINISHED_STATUSES.includes(statusNum);
  const [showMatchInfoModal, setShowMatchInfoModal] = useState(false);
  const isShowInsight = match.status == "0" && match?.insight;
  const isShowDuDoanKiemTien = getMatchHeaderType(match?.status) === "upcoming";

  const handleDataClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMatchInfoModal(true);
  };
  const handleInsightClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const slug = match?.insight?.slug;
    if (slug) window.open(`${KEOBONGVIP_DOMAIN}/soi-keo/${slug}`, "_blank", "noopener,noreferrer");
  };
  const handleDuDoanKiemTienClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const dateStr = match?.matchTime != null ? convertMatchTime(match.matchTime, "YYYY-MM-DD") : "";
    const dateParam = dateStr ? `&date=${dateStr}` : "";
    window.open(`${KEOBONGVIP_DOMAIN}?showDuDoanKiemTien=1&matchId=${match.id}${dateParam}`, "_blank", "noopener,noreferrer");
  };

  // Handicap odds 
  const handicapOdds = match.handicapOdds?.[0];
  const handicapValue = getOddValue(
    filterHandicapOdds?.instantHandicap,
    handicapOdds?.initialHandicap,
  );
  const homeHandicapOdds = getOddValue(
    filterHandicapOdds?.instantHome,
    handicapOdds?.initialHome,
  );
  const awayHandicapOdds = getOddValue(
    filterHandicapOdds?.instantAway,
    handicapOdds?.initialAway,
  );
  // Trends
  const homeHandicapTrend =
    filterHandicapOdds?.initialHomeTrend !== "unchanged"
      ? filterHandicapOdds?.initialHomeTrend
      : filterHandicapOdds?.initialHomeTrendOld || "unchanged";
  const awayHandicapTrend =
    filterHandicapOdds?.initialAwayTrend !== "unchanged"
      ? filterHandicapOdds?.initialAwayTrend
      : filterHandicapOdds?.initialAwayTrendOld || "unchanged";
    
  // Over/Under odds 
  const overUnderOdds = match.overUnderOdds?.[0] || match.odd?.overUnderOdds?.[0];
  const overUnderValue = getOddValue(
    filterOverUnderOdds?.instantOverUnder,
    overUnderOdds?.instantOverUnder || "2.50",
  );
  const overOdds = getOddValue(
    filterOverUnderOdds?.instantOver,
    overUnderOdds?.initialOver,
  );
  const underOdds = getOddValue(
    filterOverUnderOdds?.instantUnder,
    overUnderOdds?.initialUnder,
  );
  // Europe odds
  const europeOdds = getOddValue(
    filterEuropeOdds?.instantOverUnder,
    match.europeOdds?.[0],
  );
  const homeEuropeOdds = getOddValue(
    filterEuropeOdds?.instantHome,
    europeOdds?.instantHome,
  );
  const awayEuropeOdds = getOddValue(
    filterEuropeOdds?.instantAway,
    europeOdds?.instantAway,
  );
  const instantDrawOdds = getOddValue(
    filterEuropeOdds?.instantDraw,
    europeOdds?.instantDraw,
  );
  // Half-time handicap odds
  const handicapHalfOdds = match.handicapHalfOdds?.[0];
  const handicapHalfValue = getOddValue(
    filterHandicapHalfOdds?.instantHandicap,
    handicapHalfOdds?.instantHandicap,
  );
  const homeHandicapHalfOdds = getOddValue(
    filterHandicapHalfOdds?.instantHome,
    handicapHalfOdds?.instantHome,
  );
  const awayHandicapHalfOdds = getOddValue(
    filterHandicapHalfOdds?.instantAway,
    handicapHalfOdds?.instantAway,
  );
  // Trends
  const overTrend =
    filterOverUnderOdds?.initialOverTrend !== "unchanged"
      ? filterOverUnderOdds?.initialOverTrend
      : filterOverUnderOdds?.initialOverTrendOld || "unchanged";
  const underTrend =
    filterOverUnderOdds?.initialUnderTrend !== "unchanged"
      ? filterOverUnderOdds?.initialUnderTrend
      : filterOverUnderOdds?.initialUnderTrendOld || "unchanged";

  return (
    <div className="w-full min-w-[300px] bg-[var(--theme-foreground)] rounded-lg overflow-hidden border border-[var(--theme-border)] pt-1 flex flex-col gap-3 cursor-pointer transition-[border,box-shadow] hover:border-[var(--theme-primary)] hover:shadow-[0_6px_12px_color-mix(in_srgb,var(--theme-primary)_18%,transparent)]">
      <div className="flex items-center justify-between gap-2 px-3">
        <div className="min-w-0 flex-1 text-sm font-medium flex items-center gap-1 text-[var(--theme-text)]">
          <CarouselMatchTime
            liveScore={liveScore}
            status={statusNum}
            matchTime={match.matchTime}
            halfStartTime={match.halfStartTime}
            isLive={isLive}
            className={isLive ? "text-[var(--theme-primary)] font-bold" : ""}
          />
          {isLive && (
            <>
              <span>
                -
              </span>
              <div className="flex items-center gap-1 whitespace-nowrap">
                <img
                  src={ICON_CORNER}
                  alt="corner"
                  width={8}
                  height={8}
                  className="kbv-corner-icon"
                />
                <span className="text-sm font-semibold text-[var(--theme-text)]">
                  {currentHomeCorner}:{currentAwayCorner}
                </span>
              </div>
            </>
          )}
          {!isLive && (
            <>
              {" - "}
              {formattedDate} ({formattedThu})
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleDataClick}
            className="block border-0 bg-transparent p-0"
            aria-label="Dữ liệu"
          >
            <img
              src={ICON_TATICS}
              alt=""
              className="kbv-corner-icon h-5 w-5 object-contain"
              aria-hidden
            />
          </button>
          {isShowInsight && (
            <button
              type="button"
              onClick={handleInsightClick}
              className="block border-0 bg-transparent p-0 hidden"
              aria-label="Nhận Định"
            >
              <img
                src={ICON_NHANDINH}
                alt=""
                className="kbv-corner-icon h-5 w-5 object-contain"
                aria-hidden
              />
            </button>
          )}
          {isShowDuDoanKiemTien && (
            <button
              type="button"
              onClick={handleDuDoanKiemTienClick}
              className="block border-0 bg-transparent p-0 hidden"
              aria-label="Dự đoán kiếm tiền"
            >
              <img
                src={ICON_DUDOAN_KIEMTIEN}
                alt=""
                className="h-5 w-5 object-contain"
                aria-hidden
              />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1 px-3">
        <div className="flex items-center gap-1">
          {match.homeTeam?.logo && (
            <img
              src={match.homeTeam.logo}
              alt={match.homeName}
              width={24}
              height={24}
              className="rounded-full"
            />
          )}
          <span className="text-sm text-[var(--theme-text)]">
            {match.homeName}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {match.awayTeam?.logo && (
            <img
              src={match.awayTeam.logo}
              alt={match.awayName}
              width={24}
              height={24}
              className="rounded-full"
            />
          )}
          <span className="text-sm text-[var(--theme-text)]">
            {match.awayName}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 bg-[var(--theme-bg)] px-3 py-1">
        <div className="flex flex-col gap-2">
          <span className="text-xs text-[var(--theme-text-muted)] font-medium">
            Cược chấp
          </span>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 bg-[var(--theme-foreground)]">
              <div className="px-2 py-1 rounded text-xs text-[var(--theme-text-muted)] min-w-[40px] text-center">
                {formatHandicapLabel(handicapValue, true)}
              </div>
              <div className="flex items-center gap-1 ml-auto">
                <KbvOddsStatus trend={homeHandicapTrend} />
                <span
                  className={`text-sm font-medium text-[var(--theme-text)] ${colorOddsChanges(homeHandicapTrend)}`}
                >
                  {homeHandicapOdds || "-"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-[var(--theme-foreground)]">
              <div className="px-2 py-1 rounded text-xs text-[var(--theme-text-muted)] min-w-[40px] text-center">
                {formatHandicapLabel(handicapValue, false)}
              </div>
              <div className="flex items-center gap-1 ml-auto">
                <KbvOddsStatus trend={awayHandicapTrend} />
                <span
                  className={`text-sm font-medium text-[var(--theme-text)] ${colorOddsChanges(awayHandicapTrend)}`}
                >
                  {awayHandicapOdds || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-xs text-[var(--theme-text-muted)] font-medium">
            Tài/Xỉu
          </span>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2 bg-[var(--theme-foreground)]">
              <div className="px-2 py-1 rounded text-xs text-[var(--theme-text-muted)] min-w-[40px] text-center">
                {overUnderValue || "2.50"}
              </div>
              <div className="flex items-center gap-1 ml-auto">
                <KbvOddsStatus trend={overTrend} />
                <span
                  className={`text-sm font-medium text-[var(--theme-text)] ${colorOddsChanges(overTrend)}`}
                >
                  {overOdds || "-"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 bg-[var(--theme-foreground)]">
              <div className="px-2 py-1 rounded text-xs text-[var(--theme-text-muted)] min-w-[40px] text-center">
                U
              </div>
              <div className="flex items-center gap-1 ml-auto">
                <KbvOddsStatus trend={underTrend} />
                <span
                  className={`text-sm font-medium text-[var(--theme-text)] ${colorOddsChanges(underTrend)}`}
                >
                  {underOdds || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showMatchInfoModal &&
        typeof document !== "undefined" &&
        createPortal(
          <ThongTinTranDau
            isOpen
            onClose={() => setShowMatchInfoModal(false)}
            match={match}
            matchInfo={{
              formattedDate,
              formattedThu,
              formattedGio,
              isLive,
              isFinished,
            }}
            oddsInfo={{
              handicapValue,
              homeHandicapOdds,
              awayHandicapOdds,
              overUnderValue,
              overOdds,
              underOdds,
              homeEuropeOdds,
              awayEuropeOdds,
              instantDrawOdds,
              handicapHalfValue,
              homeHandicapHalfOdds,
              awayHandicapHalfOdds,
            }}
            statsInfo={{
              currentHomeScore,
              currentAwayScore,
              currentHomeHalfScore,
              currentAwayHalfScore,
              homeYellow,
              homeRed,
              awayYellow,
              awayRed,
              currentHomeCorner,
              currentAwayCorner,
            }}
          />,
          document.body,
        )}
    </div>
  );
}
